// #336 S3: the rep range of a set («10 (8-12)»). `reps` stays the target; the
// range is optional, both ends or neither, min <= reps <= max, and never on an
// AMRAP or a timed set (Q6, brief §15 rows 20 and 24). One rule, read by the
// API (stored sets), the editor's save and problem list (Draft cells), the
// schema line and the 426 detector. Keep free of React and any UI import.
import type { CellDraft, SetRowDraft } from "./programEdit.js";
import { amrapOf } from "./programEditBuild.js";
import { REPS_RANGE_MAX } from "./schemas/programV3.js";

/** What the range reads of a cell: a Draft cell, or the set table's row. */
type RangeCell = Pick<CellDraft, "reps" | "amrap" | "repsMin" | "repsMax">;

export type RepsRange = { min: number; max: number };

type StoredSet = {
  reps: number;
  repsMin?: number;
  repsMax?: number;
  amrap?: boolean;
  work?: unknown;
};

/** The set is an AMRAP: its own flag, or the slot's last-set flag on the last
 *  set. One rule for the resolver and the API's range check (#467). */
export const setWantsAmrap = (
  set: { amrap?: boolean },
  slot: { amrapLastSet?: boolean },
  onLastSet: boolean,
): boolean => set.amrap === true || (slot.amrapLastSet === true && onLastSet);

/** What is wrong with a stored set's range, or null (none, or a good one). */
export const repsRangeProblem = (
  set: StoredSet,
): "half" | "order" | "amrap" | "timed" | null => {
  const { repsMin: lo, repsMax: hi } = set;
  if (lo === undefined && hi === undefined) return null;
  if (lo === undefined || hi === undefined) return "half";
  if (set.amrap === true) return "amrap";
  if (set.work !== undefined) return "timed";
  return lo <= set.reps && set.reps <= hi ? null : "order";
};

/** One end of a range as typed: a whole number 0..REPS_RANGE_MAX, else null. */
const parseEnd = (v: string | undefined): number | null => {
  const m = /^\s*(\d{1,3})\s*$/.exec(v ?? "");
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n <= REPS_RANGE_MAX ? n : null;
};

/** Neither end typed. */
const blank = (cell: RangeCell): boolean =>
  (cell.repsMin ?? "").trim() === "" && (cell.repsMax ?? "").trim() === "";

/** The set's range does not apply: an AMRAP, or a set bound by time. The
 *  range stays on the cell, and comes back when the set does. Exported for
 *  the set table, which hides the range there. */
export const repsRangeOff = (row: Pick<SetRowDraft, "workKind">, cell: RangeCell): boolean =>
  row.workKind !== undefined || amrapOf(cell);

/** The range a Draft cell prescribes, or null: none typed, one that does not
 *  parse, or a set it does not apply to (repsRangeOff). What the save sends. */
export const cellRepsRange = (
  row: Pick<SetRowDraft, "workKind">,
  cell: RangeCell,
): RepsRange | null => {
  if (blank(cell) || repsRangeOff(row, cell)) return null;
  const min = parseEnd(cell.repsMin);
  const max = parseEnd(cell.repsMax);
  return min === null || max === null ? null : { min, max };
};

/** The cell's range cannot be saved: one end only, an end that is not a whole
 *  number, or a target outside it. Silent where the range does not apply. */
export const cellRepsRangeInvalid = (
  row: Pick<SetRowDraft, "workKind">,
  cell: RangeCell,
  reps: number | undefined,
): boolean => {
  if (blank(cell) || repsRangeOff(row, cell)) return false;
  const range = cellRepsRange(row, cell);
  return !range || reps === undefined || range.min > reps || reps > range.max;
};
